import { ReactNode, createContext, useContext, useMemo } from 'react';
import { IWedding } from '@bridebook/models/source/models/Weddings.types';
import { arrayToObject } from '@bridebook/web/lib/checklist/utils';
import { BBOrCustomSupplier } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import { IThemedComponentStyles } from '@bridebook/web/lib/wedding-website/utils/get-themed-component-styles';

/**
 * Context interface for wedding website view mode functionality.
 *
 * @interface IViewModeContext
 * @property {Object} wedding - Basic wedding information including date and partner names
 * @property {BBOrCustomSupplier[]} suppliers - Array of suppliers associated with the wedding
 * @property {Record<string, BBOrCustomSupplier>} suppliersById - Map of suppliers indexed by their ID for quick lookup
 * @property {IThemedComponentStyles} themeStyles - Styling configuration for themed components
 * @property {boolean} editMode - When true, displays floating edit buttons and section placeholders for content editing
 * @property {'page' | 'panel'} container - Determines the display context: 'page' for full-page view (mobile preview or live site), 'panel' for embedded view (desktop preview)
 * @property {Function} [clickedSupplierProfileAnalytics] - Optional callback function for tracking supplier profile click analytics
 */
export interface IViewModeContext {
  wedding: {
    date: IWedding['date'];
    partners: [string, string];
  };
  suppliers: BBOrCustomSupplier[];
  suppliersById: Record<string, BBOrCustomSupplier>;
  themeStyles: IThemedComponentStyles;
  editMode: boolean;
  container: 'page' | 'panel';
  clickedSupplierProfileAnalytics?: () => void;
}

export const ViewModeContext = createContext<Omit<IViewModeContext, 'children'> | null>(null);

// TODO perhaps we could put the whole get response in context object
export interface ViewModeContextProviderProps extends Omit<IViewModeContext, 'suppliersById'> {
  children: ReactNode;
}

export function ViewModeContextProvider({ children, ...rest }: ViewModeContextProviderProps) {
  const context = useMemo(() => {
    const suppliersById = arrayToObject(rest.suppliers);
    return {
      wedding: rest.wedding,
      suppliers: rest.suppliers,
      suppliersById,
      themeStyles: rest.themeStyles,
      editMode: rest.editMode,
      container: rest.container,
      clickedSupplierProfileAnalytics: rest.clickedSupplierProfileAnalytics,
    };
  }, [
    rest.suppliers,
    rest.wedding,
    rest.themeStyles,
    rest.editMode,
    rest.container,
    rest.clickedSupplierProfileAnalytics,
  ]);
  return <ViewModeContext.Provider value={context}>{children}</ViewModeContext.Provider>;
}

export function useViewModeSuppliers() {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('Missing ViewModeContextProvider in the tree');
  }

  return {
    byId: context.suppliersById,
    list: context.suppliers,
  };
}

export function useViewModeTheme() {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('Missing ViewModeContextProvider in the tree');
  }

  return context.themeStyles;
}

export function useViewModeEditMode() {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('Missing ViewModeContextProvider in the tree');
  }

  return context.editMode;
}

export function useViewModeContainer() {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('Missing ViewModeContextProvider in the tree');
  }

  return context.container;
}

export function useViewModeWedding() {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('Missing ViewModeContextProvider in the tree');
  }

  return context.wedding;
}

export function useViewModeAnalytics() {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewModeAnalytics must be used within a ViewModeContextProvider');
  }

  return context.clickedSupplierProfileAnalytics;
}
